// WorkAtHeight.js
import React, { useState } from "react";
import {
    Button,
    Divider,
    Grid,
    Grid2,
    Paper,
    Typography,
    TextField,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    InputLabel,
    FormControlLabel,
    Box,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker, TimePicker, DateTimePicker } from "@mui/x-date-pickers";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";

const commonCss = {
    shadeGreen: "#0f766e",
    red: "#e53e3e",
    green: "#10b981",
    labelPadding: "0.5rem",
    labelInputColor: "#6b7280",
    labelFontWeight: 600,
    inputBackgroundColor: "#fff",
    readOnlyBGColor: "#f3f4f6",
    inputBorderColor: "#d1d5db",
    paperBorderRadius: "12px",
    inputBorderRadius: "8px",
    mainLabelHeading: 700,
    normalSentenceWeight: 500,
    normalSenternceColor: "#111827",
    buttonBorderRadius: "8px",
    buttonTextColor: "#fff",
    shadeLinearGradient: "linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)",
};

const WorkAtHeight = ({ handleCloseClick } = {}) => {
    // State management for approval sections
    const [approveInfo, setApproveInfo] = useState({
        engineerName: "",
        lpiName: "",
        certificateClosedName: "",
    });
    // checklist labels
    const generalMeasuresList = [
        { name: "cordonOffArea", label: "Cordon off affected areas and display warning notices" },
        { name: "safeAccess", label: "Adequate and safe access / egress provided" },
        { name: "handrailProvided", label: "Handrails / edge restraint provided" },
        { name: "clearArea", label: "Clearing of personnel other than those involved" },
        { name: "workersBriefed", label: "Workmen briefed on hazards & preventive measures" },
        { name: "rescuePlan", label: "Emergency rescue plan available" },
        { name: "goodWeather", label: "Good weather / external environment (outdoor jobs)" },
        { name: "energyLockedOut", label: "All energy sources locked out / isolated" },
        { name: "toolsSecured", label: "Tools secured to prevent falling" },
        { name: "barricadeRequired", label: "Barricades / hoarding required" },
        { name: "equipmentSafe", label: "Nearby equipment safe for work to proceed" },
        { name: "wahCertificateAvailable", label: "Work at Height training certificates available" },
    ];

    const scaffoldingList = [
        { name: "scaffoldRigid", label: "Scaffolding erected with rigid, sturdy materials (CIDB compliance)" },
        { name: "scaffoldFloorboards", label: "Working levels equipped with full area floorboards & toe boards" },
        { name: "scaffoldInspected", label: "Scaffolding erected by competent persons and inspected" },
        { name: "scaffoldTagAvailable", label: "Scaffolding tag available and inspected weekly" },
    ];

    const roofList = [
        { name: "fragileRoof", label: "Fragile roof areas identified (asbestos, corroded sheeting, etc.)" },
        { name: "lifelineAvailable", label: "Horizontal lifeline available and in good condition" },
        { name: "harnessSecured", label: "Body harness lanyard secured to lifeline" },
    ];

    const ladderList = [
        { name: "ladderInspected", label: "Ladders inspected and secured (tie-offs)" },
        { name: "threePointContact", label: "Workers briefed to maintain 3-point contact" },
    ];

    const mewpList = [
        { name: "mewpInspected", label: "MEWP (skylift/scissor/boom) inspected prior to start" },
        { name: "operatorTrained", label: "MEWP operator trained" },
        { name: "withinSWL", label: "Loads are within SWL of the MEWP" },
        { name: "validPMA", label: "Valid PMA available for the MEWP" },
    ];

    const initialValues = {
        // contractor info
        companyDept: "",
        supervisorName: "",
        supervisorContact: "",
        workLocation: "",
        plantMachine: "",
        commenceDate: null,
        commenceTime: null,
        completionDate: null,
        completionTime: null,
        workDescription: "",

        // general measures
        // all booleans default false
        cordonOffArea: false,
        safeAccess: false,
        handrailProvided: false,
        clearArea: false,
        workersBriefed: false,
        rescuePlan: false,
        goodWeather: false,
        energyLockedOut: false,
        toolsSecured: false,
        barricadeRequired: false,
        equipmentSafe: false,
        wahCertificateAvailable: false,

        // scaffolding
        scaffoldRigid: false,
        scaffoldFloorboards: false,
        scaffoldInspected: false,
        scaffoldTagAvailable: false,

        // roof
        fragileRoof: false,
        lifelineAvailable: false,
        harnessSecured: false,

        // ladder
        ladderInspected: false,
        threePointContact: false,

        // MEWP
        mewpInspected: false,
        operatorTrained: false,
        withinSWL: false,
        validPMA: false,

        // PPE + others
        ppeRequired: "",
        doubleLanyard: false,
        otherPPE: "",
        workersTrainedPPE: false,
        buddyPresent: false,
        adequateLighting: false,
        otherPrecautions: "",
        otherHazards: "",
    };

    const validationSchema = Yup.object().shape({
        companyDept: Yup.string().required("This field is required"),
        supervisorName: Yup.string().required("This field is required"),
        supervisorContact: Yup.string().required("This field is required"),
        workLocation: Yup.string().required("This field is required"),
        commenceDate: Yup.date().nullable().required("Commencement date is required"),
        commenceTime: Yup.date().nullable().required("Commencement time is required"),
        completionDate: Yup.date().nullable().required("Completion date is required"),
        completionTime: Yup.date().nullable().required("Completion time is required"),
        workDescription: Yup.string().required("Work description is required"),
        // Additional conditional validations could go here
    });

    // placeholder: replace with actual API call when available
    const sendPayload = async (payload) => {
        console.log("Prepared payload (grouped JSON):", payload);
        // Example: axios.post("/api/workAtHeight", payload)
        // but as requested we do not perform the call here
        // Return success simulation
        return { success: true };
    };

    const buildGroupedPayload = (values) => {
        return {
            meta: {
                permitTitle: "Work At Height Permit",
                createdAt: new Date().toISOString(),
            },
            contractorInfo: {
                companyDept: values.companyDept,
                supervisorName: values.supervisorName,
                supervisorContact: values.supervisorContact,
                workLocation: values.workLocation,
                plantMachine: values.plantMachine,
                commenceDate: values.commenceDate ? dayjs(values.commenceDate).format("YYYY-MM-DD") : null,
                commenceTime: values.commenceTime ? dayjs(values.commenceTime).format("HH:mm") : null,
                completionDate: values.completionDate ? dayjs(values.completionDate).format("YYYY-MM-DD") : null,
                completionTime: values.completionTime ? dayjs(values.completionTime).format("HH:mm") : null,
                workDescription: values.workDescription,
            },
            generalMeasures: {
                cordonOffArea: !!values.cordonOffArea,
                safeAccess: !!values.safeAccess,
                handrailProvided: !!values.handrailProvided,
                clearArea: !!values.clearArea,
                workersBriefed: !!values.workersBriefed,
                rescuePlan: !!values.rescuePlan,
                goodWeather: !!values.goodWeather,
                energyLockedOut: !!values.energyLockedOut,
                toolsSecured: !!values.toolsSecured,
                barricadeRequired: !!values.barricadeRequired,
                equipmentSafe: !!values.equipmentSafe,
                wahCertificateAvailable: !!values.wahCertificateAvailable,
            },
            scaffoldingSafety: {
                scaffoldRigid: !!values.scaffoldRigid,
                scaffoldFloorboards: !!values.scaffoldFloorboards,
                scaffoldInspected: !!values.scaffoldInspected,
                scaffoldTagAvailable: !!values.scaffoldTagAvailable,
            },
            workingOnRoof: {
                fragileRoof: !!values.fragileRoof,
                lifelineAvailable: !!values.lifelineAvailable,
                harnessSecured: !!values.harnessSecured,
            },
            ladderSafety: {
                ladderInspected: !!values.ladderInspected,
                threePointContact: !!values.threePointContact,
            },
            mewpSafety: {
                mewpInspected: !!values.mewpInspected,
                operatorTrained: !!values.operatorTrained,
                withinSWL: !!values.withinSWL,
                validPMA: !!values.validPMA,
            },
            ppeRequirements: {
                ppeRequired: values.ppeRequired || "",
                doubleLanyard: !!values.doubleLanyard,
                otherPPE: values.otherPPE || "",
                workersTrainedPPE: !!values.workersTrainedPPE,
                buddyPresent: !!values.buddyPresent,
                adequateLighting: !!values.adequateLighting,
                otherPrecautions: values.otherPrecautions || "",
                otherHazards: values.otherHazards || "",
            },
        };
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    const payload = buildGroupedPayload(values);
                    await sendPayload(payload);
                    setSubmitting(false);
                    // optional: call handleCloseClick() if provided
                    if (typeof handleCloseClick === "function") handleCloseClick();
                }}
            >
                {({ values, handleChange, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                        <Typography variant="h5" fontWeight="bold">
                                            Work At Height Permit
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    {/* Contractor Info */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Company / Department
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                name="companyDept"
                                                value={values.companyDept}
                                                onChange={handleChange}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                sx={{ mb: 1 }}
                                            />
                                            <ErrorMessage name="companyDept" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Supervisor Name & Contact No
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                name="supervisorName"
                                                value={values.supervisorName}
                                                onChange={handleChange}
                                                placeholder="Name"
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                sx={{ mb: 1 }}
                                            />
                                            <TextField
                                                name="supervisorContact"
                                                value={values.supervisorContact}
                                                onChange={handleChange}
                                                placeholder="Contact number"
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                            />
                                            <ErrorMessage name="supervisorName" component="div" style={{ color: commonCss.red }} />
                                            <ErrorMessage name="supervisorContact" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Work Location
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="workLocation" value={values.workLocation} onChange={handleChange} fullWidth size="small" variant="outlined" />
                                            <ErrorMessage name="workLocation" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Plant / Machine
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="plantMachine" value={values.plantMachine} onChange={handleChange} fullWidth size="small" variant="outlined" />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Commencement Date
                                                </Typography>
                                            </InputLabel>
                                            <DatePicker
                                                value={values.commenceDate}
                                                onChange={(v) => setFieldValue("commenceDate", v)}
                                                format="DD/MM/YYYY"
                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                            />
                                            <ErrorMessage name="commenceDate" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Commencement Time
                                                </Typography>
                                            </InputLabel>
                                            <TimePicker
                                                value={values.commenceTime}
                                                onChange={(v) => setFieldValue("commenceTime", v)}
                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                            />
                                            <ErrorMessage name="commenceTime" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Completion Date
                                                </Typography>
                                            </InputLabel>
                                            <DatePicker
                                                value={values.completionDate}
                                                onChange={(v) => setFieldValue("completionDate", v)}
                                                format="DD/MM/YYYY"
                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                            />
                                            <ErrorMessage name="completionDate" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Completion Time
                                                </Typography>
                                            </InputLabel>
                                            <TimePicker
                                                value={values.completionTime}
                                                onChange={(v) => setFieldValue("completionTime", v)}
                                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                                            />
                                            <ErrorMessage name="completionTime" component="div" style={{ color: commonCss.red }} />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Work Description
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                name="workDescription"
                                                value={values.workDescription}
                                                onChange={handleChange}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                size="small"
                                                variant="outlined"
                                            />
                                            <ErrorMessage name="workDescription" component="div" style={{ color: commonCss.red }} />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Checklists - General Measures */}
                            <Grid item xs={12}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        SECTION I: INSPECTION BY CONTRACTOR — General Measures
                                    </Typography>
                                    <List>
                                        {generalMeasuresList.map((item) => (
                                            <ListItem key={item.name} sx={{ py: 0 }}>
                                                <ListItemText primary={item.label} />
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <Checkbox
                                                        checked={!!values[item.name]}
                                                        onChange={(e) => setFieldValue(item.name, e.target.checked)}
                                                    />
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Scaffolding */}
                            <Grid item xs={12} md={6}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        Scaffolding Safety
                                    </Typography>
                                    <List>
                                        {scaffoldingList.map((item) => (
                                            <ListItem key={item.name} sx={{ py: 0 }}>
                                                <ListItemText primary={item.label} />
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <Checkbox checked={!!values[item.name]} onChange={(e) => setFieldValue(item.name, e.target.checked)} />
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Roof */}
                            <Grid item xs={12} md={6}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        Working on Roof
                                    </Typography>
                                    <List>
                                        {roofList.map((item) => (
                                            <ListItem key={item.name} sx={{ py: 0 }}>
                                                <ListItemText primary={item.label} />
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <Checkbox checked={!!values[item.name]} onChange={(e) => setFieldValue(item.name, e.target.checked)} />
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Ladder & MEWP */}
                            <Grid item xs={12} md={6}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        Ladder Safety
                                    </Typography>
                                    <List>
                                        {ladderList.map((item) => (
                                            <ListItem key={item.name} sx={{ py: 0 }}>
                                                <ListItemText primary={item.label} />
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <Checkbox checked={!!values[item.name]} onChange={(e) => setFieldValue(item.name, e.target.checked)} />
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        MEWP (Mobile Elevated Work Platform)
                                    </Typography>
                                    <List>
                                        {mewpList.map((item) => (
                                            <ListItem key={item.name} sx={{ py: 0 }}>
                                                <ListItemText primary={item.label} />
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    <Checkbox checked={!!values[item.name]} onChange={(e) => setFieldValue(item.name, e.target.checked)} />
                                                </ListItemIcon>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* PPE */}
                            <Grid item xs={12}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red}>
                                        Personal Protective Equipment (PPE)
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    What PPE is required?
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="ppeRequired" value={values.ppeRequired} onChange={handleChange} fullWidth size="small" variant="outlined" />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Others (specify)
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="otherPPE" value={values.otherPPE} onChange={handleChange} fullWidth size="small" variant="outlined" />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <FormControlLabel
                                                control={<Checkbox checked={values.doubleLanyard} onChange={(e) => setFieldValue("doubleLanyard", e.target.checked)} />}
                                                label="Safety Harness with Double Lanyard"
                                                sx={{ mt: 1 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <FormControlLabel
                                                control={<Checkbox checked={values.workersTrainedPPE} onChange={(e) => setFieldValue("workersTrainedPPE", e.target.checked)} />}
                                                label="Workers trained in PPE use"
                                                sx={{ mt: 1 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <FormControlLabel
                                                control={<Checkbox checked={values.buddyPresent} onChange={(e) => setFieldValue("buddyPresent", e.target.checked)} />}
                                                label="Standby buddy present"
                                                sx={{ mt: 1 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <FormControlLabel
                                                control={<Checkbox checked={values.adequateLighting} onChange={(e) => setFieldValue("adequateLighting", e.target.checked)} />}
                                                label="Adequate lighting available"
                                                sx={{ mt: 1 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Other precautions needed
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="otherPrecautions" value={values.otherPrecautions} onChange={handleChange} fullWidth multiline rows={2} size="small" variant="outlined" />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <InputLabel sx={{ pl: commonCss.labelPadding }}>
                                                <Typography variant="body2" fontWeight={commonCss.labelFontWeight} color={commonCss.labelInputColor}>
                                                    Any other hazards to watch for
                                                </Typography>
                                            </InputLabel>
                                            <TextField name="otherHazards" value={values.otherHazards} onChange={handleChange} fullWidth multiline rows={2} size="small" variant="outlined" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Permit Invalidation Conditions */}
                            <Grid item xs={12}>
                                <Paper elevation={3} sx={{ p: 2, borderRadius: commonCss.paperBorderRadius }}>
                                    <Typography variant="body1" fontWeight={commonCss.mainLabelHeading} color={commonCss.red} sx={{ mb: 2 }}>
                                        Note: Changes in conditions below will invalidate this permit.
                                    </Typography>
                                    <List sx={{ listStyleType: "decimal", pl: 4 }}>
                                        <ListItem sx={{ display: "list-item", py: 0.5 }}>
                                            <ListItemText primary="New work activities have been identified." />
                                        </ListItem>
                                        <ListItem sx={{ display: "list-item", py: 0.5 }}>
                                            <ListItemText primary="New personnel join the work activity." />
                                        </ListItem>
                                        <ListItem sx={{ display: "list-item", py: 0.5 }}>
                                            <ListItemText primary="Start of work is delayed or break in the work activity occurs for more than 30 minutes." />
                                        </ListItem>
                                        <ListItem sx={{ display: "list-item", py: 0.5 }}>
                                            <ListItemText primary="Work extends beyond the authorized duration (e.g., 8 to 12-hour shift)." />
                                        </ListItem>
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Approval Sections */}
                            <Grid item xs={12}>
                                <Grid2 container size={12} spacing={2}>
                                    {/* Engineer Section */}
                                    <Grid2 container size={{ xs: 12, md: 4 }}>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                borderRadius: commonCss.paperBorderRadius,
                                                p: 1.5,
                                                background: "white",
                                                width: "100%",
                                            }}
                                        >
                                            <Grid2 container size={12} spacing={"1rem"}>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.mainLabelHeading}
                                                        color={commonCss.red}
                                                        textAlign={"center"}
                                                    >
                                                        Area Owner
                                                    </Typography>
                                                    <Divider
                                                        sx={{
                                                            borderColor: commonCss.red,
                                                            borderWidth: 1,
                                                            width: "100%",
                                                            mt: 1,
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.normalSentenceWeight}
                                                        color={commonCss.normalSenternceColor}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        I certify that I have reviewed the proposed work and I am satisfied that it is adequately described on the permit to work and that the precautions detailed are adequate and clearly defined
                                                    </Typography>
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Name
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        value={approveInfo.engineerName}
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                backgroundColor: commonCss.readOnlyBGColor,
                                                                borderRadius: commonCss.inputBorderRadius,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: commonCss.inputBorderColor,
                                                            },
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Date/Time
                                                        </Typography>
                                                    </InputLabel>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DemoItem>
                                                            <DateTimePicker
                                                                ampm={false}
                                                                format="DD/MM/YYYY HH:mm"
                                                                readOnly
                                                                slotProps={{
                                                                    textField: {
                                                                        size: "small",
                                                                        variant: "outlined",
                                                                    },
                                                                }}
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: commonCss.inputBorderRadius,
                                                                        backgroundColor: commonCss.readOnlyBGColor,
                                                                    },
                                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                                        borderColor: commonCss.inputBorderColor,
                                                                    },
                                                                }}
                                                                fullWidth
                                                            />
                                                        </DemoItem>
                                                    </LocalizationProvider>
                                                </Grid2>
                                            </Grid2>
                                        </Paper>
                                    </Grid2>

                                    {/* LPI Section */}
                                    <Grid2 container size={{ xs: 12, md: 4 }}>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                borderRadius: commonCss.paperBorderRadius,
                                                p: 1.5,
                                                background: "white",
                                                width: "100%",
                                            }}
                                        >
                                            <Grid2 container size={12} spacing={"1rem"}>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.mainLabelHeading}
                                                        color={commonCss.red}
                                                        textAlign={"center"}
                                                    >
                                                        EOSH Officer
                                                    </Typography>
                                                    <Divider
                                                        sx={{
                                                            borderColor: commonCss.red,
                                                            borderWidth: 1,
                                                            width: "100%",
                                                            mt: 1,
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.normalSentenceWeight}
                                                        color={commonCss.normalSenternceColor}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Work at Height permit to work is allowed on this date.
                                                    </Typography>
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Name
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        value={approveInfo.lpiName}
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                backgroundColor: commonCss.readOnlyBGColor,
                                                                borderRadius: commonCss.inputBorderRadius,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: commonCss.inputBorderColor,
                                                            },
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Date/Time
                                                        </Typography>
                                                    </InputLabel>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DemoItem>
                                                            <DateTimePicker
                                                                ampm={false}
                                                                readOnly
                                                                format="DD/MM/YYYY HH:mm"
                                                                slotProps={{
                                                                    textField: {
                                                                        size: "small",
                                                                        variant: "outlined",
                                                                    },
                                                                }}
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: commonCss.inputBorderRadius,
                                                                        backgroundColor: commonCss.readOnlyBGColor,
                                                                    },
                                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                                        borderColor: commonCss.inputBorderColor,
                                                                    },
                                                                }}
                                                                fullWidth
                                                            />
                                                        </DemoItem>
                                                    </LocalizationProvider>
                                                </Grid2>
                                            </Grid2>
                                        </Paper>
                                    </Grid2>

                                    {/* Permit Closure Section */}
                                    <Grid2 container size={{ xs: 12, md: 4 }}>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                borderRadius: commonCss.paperBorderRadius,
                                                p: 1.5,
                                                background: "white",
                                                width: "100%",
                                            }}
                                        >
                                            <Grid2 container size={12} spacing={"1rem"}>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.mainLabelHeading}
                                                        color={commonCss.red}
                                                        textAlign={"center"}
                                                    >
                                                        Permit Closure
                                                    </Typography>
                                                    <Divider
                                                        sx={{
                                                            borderColor: commonCss.red,
                                                            borderWidth: 1,
                                                            width: "100%",
                                                            mt: 1,
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={commonCss.normalSentenceWeight}
                                                        color={commonCss.normalSenternceColor}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Work at Height permit and certificate are withdrawn:
                                                    </Typography>
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Permit Holder Name
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                        value={approveInfo.certificateClosedName}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                backgroundColor: commonCss.readOnlyBGColor,
                                                                borderRadius: commonCss.inputBorderRadius,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: commonCss.inputBorderColor,
                                                            },
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Permit Holder Date/Time
                                                        </Typography>
                                                    </InputLabel>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DemoItem>
                                                            <DateTimePicker
                                                                ampm={false}
                                                                format="DD/MM/YYYY HH:mm"
                                                                readOnly
                                                                slotProps={{
                                                                    textField: {
                                                                        size: "small",
                                                                        variant: "outlined",
                                                                    },
                                                                }}
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: commonCss.inputBorderRadius,
                                                                        backgroundColor: commonCss.readOnlyBGColor,
                                                                    },
                                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                                        borderColor: commonCss.inputBorderColor,
                                                                    },
                                                                }}
                                                                fullWidth
                                                            />
                                                        </DemoItem>
                                                    </LocalizationProvider>
                                                </Grid2>
                                                <Grid2 size={{ xs: 12 }}>
                                                    <InputLabel>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={commonCss.labelFontWeight}
                                                            color={commonCss.labelInputColor}
                                                        >
                                                            Reason
                                                        </Typography>
                                                    </InputLabel>
                                                    <TextField
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        multiline
                                                        minRows={2}
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                backgroundColor: commonCss.readOnlyBGColor,
                                                                borderRadius: commonCss.inputBorderRadius,
                                                            },
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: commonCss.inputBorderColor,
                                                            },
                                                        }}
                                                    />
                                                </Grid2>
                                            </Grid2>
                                        </Paper>
                                    </Grid2>
                                </Grid2>
                            </Grid>

                            {/* Action Buttons */}
                            <Grid item xs={12} sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    onClick={() => {
                                        // reset / close logic
                                        if (typeof handleCloseClick === "function") handleCloseClick();
                                    }}
                                >
                                    Cancel
                                </Button>

                                <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} sx={{ bgcolor: commonCss.green }}>
                                    Submit
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </LocalizationProvider>
    );
};

export default WorkAtHeight;
